<template>
    <div v-if="loaded === 4" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
        </div>

        <div class="financial-settings-disclaimer">{{ $LANG.Get('texts.financialSettingsDisclaimer') }}</div>

        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.defaultSettings') }}</h2>
            <NevsSelect v-model="client.default_measurement_unit_id" :ajax="'select/measurement-units'"
                        :label="$LANG.Get('fields.defaultMeasurementUnitId') + ':'"
                        :nullable="true"
                        class="half"></NevsSelect>
            <NevsSelect v-model="client.default_payment_method_id" :ajax="'select/payment-methods'"
                        :label="$LANG.Get('fields.defaultPaymentMethodId') + ':'"
                        :nullable="true"
                        class="half"></NevsSelect>
            <NevsSelect v-model="client.default_kpd_code" :ajax="'select/product-codes'"
                        :label="$LANG.Get('fields.defaultKpdCode') + ':'"
                        :nullable="true"
                        :minimumSearchLength="3"></NevsSelect>
        </NevsCard>

        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.fiskalSettings') }}</h2>
            <NevsUpload v-model="client.fiskal_certificate" :label="$LANG.Get('fields.fiskalCertificate') + ':'"
                        class="half"></NevsUpload>
            <p v-if="client.fiskal_password_set" class="disclaimer saved-password-disclaimer">{{ $LANG.Get('labels.passwordSaved') }}</p>
            <NevsTextField v-model="newFiskalPassword" :label="$LANG.Get('fields.fiskalPassword') + ':'"
                           class="half"></NevsTextField>
            <div class="disclaimer" v-html="$LANG.Get('texts.f1SettingsDisclaimer')"></div>
        </NevsCard>

        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.eInvoiceSettings') }}</h2>
            <NevsSelect :ajax="'select/e-invoice-middlemen'" v-model="client.e_invoices_middleman" :label="$LANG.Get('fields.eInvoicesMiddleman') + ':'"></NevsSelect>
            <NevsTextField v-model="client.e_invoices_token" :label="$LANG.Get('fields.eInvoicesToken') + ':'"></NevsTextField>
            <div class="disclaimer" v-html="$LANG.Get('texts.f2SettingsDisclaimer')"></div>
        </NevsCard>

        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.invoicesSettings') }}</h2>
            <NevsNumberField v-model="client.invoice_due_days_person" :decimal-places="0"
                             :label="$LANG.Get('fields.invoiceDueDaysPerson') + ':'"
                             class="half"></NevsNumberField>
            <NevsNumberField v-model="client.invoice_due_days_company" :decimal-places="0"
                             :label="$LANG.Get('fields.invoiceDueDaysCompany') + ':'"
                             class="half"></NevsNumberField>
            <NevsTextField v-model="client.invoice_place_of_issue"
                           :label="$LANG.Get('fields.invoicePlaceOfIssue') + ':'"
                           class="half"></NevsTextField>
            <NevsTextArea v-model="client.invoice_footer_template_hr"
                          :label="$LANG.Get('fields.invoiceFooterTemplateHr') + ':'"></NevsTextArea>
            <NevsTextArea v-model="client.invoice_footer_template_en"
                          :label="$LANG.Get('fields.invoiceFooterTemplateEn') + ':'"></NevsTextArea>
            <NevsSelect v-model="client.invoice_default_customer_id" :ajax="'select/customers'" class="half"
                        :label="$LANG.Get('fields.defaultCustomerId') + ':'" :nullable="true"></NevsSelect>
            <div v-if="paymentMethods.length && documentFormats.length" class="document-formats-per-payment">
                <NevsSelect v-for="paymentMethod in paymentMethods" :key="paymentMethod.value"
                            v-model="client['invoice_document_format_id_' + paymentMethod.value]"
                            :options="documentFormats"
                            :label="$LANG.Get('labels.documentFormatFor') + paymentMethod.label"
                            class="half"></NevsSelect>
            </div>
            <table class="sequences">
                <thead>
                <tr>
                    <th colspan="8">{{ $LANG.Get('labels.invoiceSequences') }}</th>
                </tr>
                <tr>
                    <th class="sequences-actions"></th>
                    <th>{{ $LANG.Get('fields.id') }}</th>
                    <th>{{ $LANG.Get('fields.format') }}</th>
                    <th class="sequences-location-column">{{ $LANG.Get('fields.location') }}</th>
                    <th class="sequences-device-column">{{ $LANG.Get('fields.device') }}</th>
                    <th class="sequences-checkbox-column">{{ $LANG.Get('fields.fiskal') }}</th>
                    <th class="sequences-checkbox-column">{{ $LANG.Get('fields.defaultM') }}</th>
                    <th class="sequences-checkbox-column">{{ $LANG.Get('fields.activeM') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(sequence, key) in sequences.invoice" :key="key">
                    <td class="sequences-actions">
                        <span v-if="!sequence.readonly" :title="$LANG.Get('tooltips.removeSequence')"
                              @click="removeSequence('invoice', key)"><i class="fa-solid fa-trash"></i></span>&nbsp;
                        <span v-if="sequence.id !== 0" :title="$LANG.Get('tooltips.setNextNumber')"
                              @click="setNext('invoice', key)"><i class="fa-solid fa-pen"></i></span>
                    </td>
                    <td>
                        <span v-if="sequence.id!=''">S{{ sequence.id }}</span>
                    </td>
                    <td>
                        <NevsTextField v-model="sequence.format" :readonly="sequence.readonly"></NevsTextField><br />
                        <div class="sequence-next-label">{{ $LANG.Get('labels.nextSequenceNumber') }} {{ sequence.next }}</div>
                    </td>
                    <td>
                        <NevsTextField v-model="sequence.location" :readonly="sequence.readonly"></NevsTextField>
                    </td>
                    <td>
                        <NevsTextField v-model="sequence.device" :readonly="sequence.readonly"></NevsTextField>
                    </td>
                    <td class="sequences-checkbox-column">
                        <NevsCheckbox v-model="sequence.fiskal"></NevsCheckbox>
                    </td>
                    <td class="sequences-checkbox-column">
                        <NevsCheckbox v-model="sequence.default"></NevsCheckbox>
                    </td>
                    <td class="sequences-checkbox-column">
                        <NevsCheckbox v-model="sequence.active"></NevsCheckbox>
                    </td>
                </tr>
                <tr>
                    <td colspan="8">
                        <NevsButton @click="addSequence('invoice')"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
                    </td>
                </tr>
                </tbody>
            </table>
            <div v-if="invoiceSequencesError!==''" class="disclaimer nevs-text-error">{{
                    invoiceSequencesError
                }}
            </div>
            <div v-if="invoiceSequencesWarning!==''" class="disclaimer nevs-text-warning">{{
                    invoiceSequencesWarning
                }}
            </div>
            <div class="disclaimer" v-html="$LANG.Get('texts.sequencesDisclaimerInvoice')"></div>
            <NevsCheckbox v-if="!client.tax_system" v-model="client.ignore_b2b_f1" :label="$LANG.Get('fields.ignoreB2bF1')"></NevsCheckbox>
        </NevsCard>

        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.offersSettings') }}</h2>
            <NevsNumberField v-model="client.offer_valid_days" :decimal-places="0"
                             :label="$LANG.Get('fields.offerValidUntil') + ':'"
                             class="half"></NevsNumberField>
            <NevsTextArea v-model="client.offer_footer_template_hr"
                          :label="$LANG.Get('fields.offerFooterTemplateHr') + ':'"></NevsTextArea>
            <NevsTextArea v-model="client.offer_footer_template_en"
                          :label="$LANG.Get('fields.offerFooterTemplateEn') + ':'"></NevsTextArea>
            <table class="sequences">
                <thead>
                <tr>
                    <th colspan="7">{{ $LANG.Get('labels.offerSequences') }}</th>
                </tr>
                <tr>
                    <th class="sequences-actions"></th>
                    <th>{{ $LANG.Get('fields.id') }}</th>
                    <th>{{ $LANG.Get('fields.format') }}</th>
                    <th class="sequences-location-column">{{ $LANG.Get('fields.location') }}</th>
                    <th class="sequences-device-column">{{ $LANG.Get('fields.device') }}</th>
                    <th class="sequences-checkbox-column">{{ $LANG.Get('fields.defaultM') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(sequence, key) in sequences.offer" :key="key">
                    <td class="sequences-actions">
                        <span v-if="!sequence.readonly" :title="$LANG.Get('tooltips.removeSequence')"
                              @click="removeSequence('offer', key)"><i
                            class="fa-solid fa-trash"></i></span>&nbsp;
                        <span v-if="sequence.id !== 0" :title="$LANG.Get('tooltips.setNextNumber')"
                              @click="setNext('offer', key)"><i class="fa-solid fa-pen"></i></span>
                    </td>
                    <td>
                        <span v-if="sequence.id!=''">S{{ sequence.id }}</span>
                    </td>
                    <td>
                        <NevsTextField v-model="sequence.format" :readonly="sequence.readonly"></NevsTextField>
                        <div class="sequence-next-label">{{ $LANG.Get('labels.nextSequenceNumber') }} {{ sequence.next }}</div>
                    </td>
                    <td>
                        <NevsTextField v-model="sequence.location" :readonly="sequence.readonly"></NevsTextField>
                    </td>
                    <td>
                        <NevsTextField v-model="sequence.device" :readonly="sequence.readonly"></NevsTextField>
                    </td>
                    <td class="sequences-checkbox-column">
                        <NevsCheckbox v-model="sequence.default"></NevsCheckbox>
                    </td>
                </tr>
                <tr>
                    <td colspan="7">
                        <NevsButton @click="addSequence('offer')"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
                    </td>
                </tr>
                </tbody>
            </table>
            <div v-if="offerSequencesError!==''" class="disclaimer nevs-text-error">{{
                    offerSequencesError
                }}
            </div>
            <div class="disclaimer">{{ $LANG.Get('texts.sequencesDisclaimerOffer') }}</div>
        </NevsCard>
        <NevsCard class="form" v-if="$store.state.user.client.plus">
            <h2>{{ $LANG.Get('labels.receiptNoteSettings') }}</h2>
            <table class="sequences">
                <thead>
                <tr>
                    <th colspan="4">{{ $LANG.Get('labels.receiptNoteSequences') }}</th>
                </tr>
                <tr>
                    <th class="sequences-actions"></th>
                    <th>{{ $LANG.Get('fields.id') }}</th>
                    <th>{{ $LANG.Get('fields.format') }}</th>
                    <th class="sequences-checkbox-column">{{ $LANG.Get('fields.defaultM') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(sequence, key) in sequences.receipt_note" :key="key">
                    <td class="sequences-actions">
                        <span v-if="!sequence.readonly" :title="$LANG.Get('tooltips.removeSequence')" @click="removeSequence('receipt_note', key)"><i class="fa-solid fa-trash"></i></span>&nbsp;
                        <span v-if="sequence.id !== 0" :title="$LANG.Get('tooltips.setNextNumber')" @click="setNext('receipt_note', key)"><i class="fa-solid fa-pen"></i></span>
                    </td>
                    <td>
                        <span v-if="sequence.id!=''">S{{ sequence.id }}</span>
                    </td>
                    <td>
                        <NevsTextField v-model="sequence.format" :readonly="sequence.readonly"></NevsTextField>
                        <div class="sequence-next-label">{{ $LANG.Get('labels.nextSequenceNumber') }} {{ sequence.next }}</div>
                    </td>
                    <td class="sequences-checkbox-column">
                        <NevsCheckbox v-model="sequence.default"></NevsCheckbox>
                    </td>
                </tr>
                <tr>
                    <td colspan="4">
                        <NevsButton @click="addSequence('receipt_note')"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
                    </td>
                </tr>
                </tbody>
            </table>
            <div class="disclaimer">{{ $LANG.Get('texts.sequencesDisclaimerReceiptNote') }}</div>
            <div v-if="receiptNoteSequencesError!==''" class="disclaimer nevs-text-error">{{
                    receiptNoteSequencesError
                }}
            </div>
        </NevsCard>
        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.ppoSettings') }}</h2>
            <NevsTextField v-model="client.ppo_template_hr"
                          :label="$LANG.Get('fields.ppoTemplateHr') + ':'"></NevsTextField>
            <NevsTextField v-model="client.ppo_template_en"
                          :label="$LANG.Get('fields.ppoTemplateEn') + ':'"></NevsTextField>
        </NevsCard>
        <NevsCard class="templates" v-html="$LANG.Get('texts.invoiceOfferTemplateHelp')"></NevsCard>
    </div>
</template>

<script>
import Client from "@/models/Client.js";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsTextArea from "@/components/nevs/NevsTextArea.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsUpload from "@/components/nevs/NevsUpload.vue";

export default {
    name: "FinancialSettings",
    components: {
        NevsUpload,
        NevsCheckbox,
        NevsSelect,
        NevsTextArea,
        NevsTextField,
        NevsNumberField,
        NevsCard,
        NevsButton
    },
    data() {
        return {
            loaded: 0,
            client: new Client(),
            clientValidation: new Client({}, true),
            paymentMethods: [],
            documentFormats: [],
            newFiskalPassword: '',
            sequences: {
                invoice: [],
                offer: [],
                receipt_note: []
            },
            sequenceTemplates: {
                invoice: {
                    id: 0,
                    format: '%slijed%-%prostor%-%uredaj%',
                    location: '01',
                    device: '01',
                    fiskal: false,
                    default: false
                },
                offer: {
                    id: 0,
                    format: '%slijed%-%prostor%-%uredaj%',
                    location: '01',
                    device: '01',
                    fiskal: false,
                    default: false
                },
                receipt_note: {
                    id: 0,
                    format: '%godina4%-%slijed%',
                    default: false
                }
            },
            invoiceSequencesError: '',
            offerSequencesError: '',
            receiptNoteSequencesError: ''
        }
    },
    computed: {
        // %slijed2%, %slijed3% and %slijed4% are just zero padded variants of %slijed%, so any of them counts.
        invoiceSequencesWarning() {
            for (let sequence of this.sequences.invoice) {
                if (!sequence.active) continue;
                let format = sequence.format;
                let hasSequenceNumber = format.includes('%slijed%')
                    || format.includes('%slijed2%')
                    || format.includes('%slijed3%')
                    || format.includes('%slijed4%');
                if (!hasSequenceNumber || !format.includes('%prostor%') || !format.includes('%uredaj%')) {
                    return this.$LANG.Get('labels.incompleteInvoiceSequenceFormat');
                }
            }
            return '';
        }
    },
    methods: {
        setNext(type, key) {
            let vm = this;
            this.$API.APICall('get', type.split('_').join('-') + '-sequences/' + this.sequences[type][key].id, {}, (data, success) => {
                if (success) {
                    let message = vm.$LANG.Get('alerts.changeNumber');
                    message = message.replace('%next_number%', data.next);
                    vm.$LOCAL_BUS.TriggerEvent('popup', {
                        type: 'input', text: message, callback: (response) => {
                            if (response !== null) {
                                this.$API.APICall('put', type.split('_').join('-') + '-sequences/' + this.sequences[type][key].id, {next: response}, (data, success) => {
                                    if (!success) {
                                        vm.$HELPERS.HandleServerError(vm, data);
                                    } else {
                                        vm.$API.APICall('get', 'invoice-sequences', {}, (data, success) => {
                                            if (success) {
                                                vm.sequences.invoice = data;
                                            }
                                        });
                                        vm.$API.APICall('get', 'offer-sequences', {}, (data, success) => {
                                            if (success) {
                                                vm.sequences.offer = data;
                                            }
                                        });
                                        vm.$API.APICall('get', 'receipt-note-sequences', {}, (data, success) => {
                                            if (success) {
                                                vm.sequences.receipt_note = data;
                                            }
                                        });
                                    }
                                });
                            }
                        }
                    });
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        removeSequence(type, key) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.sequenceDeletionQuestion'), callback: (response) => {
                    if (response) {
                        vm.sequences[type].splice(key, 1);
                    }
                }
            });
        },
        addSequence(type) {
                    this.sequences[type].push(JSON.parse(JSON.stringify(this.sequenceTemplates[type])));
        },
        initDocumentFormatMap() {
            for (let paymentMethod of this.paymentMethods) {
                let field = 'invoice_document_format_id_' + paymentMethod.value;
                if (this.client[field] === undefined || this.client[field] === null) {
                    this.client[field] = 1;
                }
            }
        },
        saveClick() {
            let valid = true;

            let invoiceSequenceDefaults = 0;
            let invoiceFiskalSequenceExists = false;
            let invoiceSequenceDuplicatesActive = false;
            let invoiceSequenceStrings = [];
            for (let sequence of this.sequences.invoice) {
                if (sequence.default) {
                    invoiceSequenceDefaults++;
                }
                if (sequence.fiskal) {
                    invoiceFiskalSequenceExists = true;
                }
                if (sequence.active) {
                    let sequenceString = sequence.location + '|' + sequence.device;
                    if (invoiceSequenceStrings.includes(sequenceString)) {
                        invoiceSequenceDuplicatesActive = true;
                    } else {
                        invoiceSequenceStrings.push(sequenceString);
                    }
                }
            }
            if (invoiceSequenceDuplicatesActive) {
                valid = false;
                this.invoiceSequencesError = this.$LANG.Get('labels.activeDuplicates');
                this.$LOCAL_BUS.TriggerEvent('popup', {
                    text: this.$LANG.Get('alerts.sequencesActiveDuplicates'),
                    type: 'alert'
                });
            } else if (invoiceSequenceDefaults > 1) {
                valid = false;
                this.invoiceSequencesError = this.$LANG.Get('labels.tooManyDefaults');
                this.$LOCAL_BUS.TriggerEvent('popup', {
                    text: this.$LANG.Get('alerts.sequencesTooManyDefaults'),
                    type: 'alert'
                });
            } else if (invoiceFiskalSequenceExists && (!this.client.fiskal_password_set && this.newFiskalPassword === '' || this.client.fiskal_certificate.name === "")) {
                valid = false;
                this.$LOCAL_BUS.TriggerEvent('popup', {
                    text: this.$LANG.Get('alerts.sequencesFiskalFail'),
                    type: 'alert'
                })
            } else {
                this.invoiceSequencesError = '';
            }

            let offerSequenceDefaults = 0;
            for (let sequence of this.sequences.offer) {
                if (sequence.default) {
                    offerSequenceDefaults++;
                }
            }
            if (offerSequenceDefaults > 1) {
                valid = false;
                this.offerSequencesError = this.$LANG.Get('labels.tooManyDefaults');
                this.$LOCAL_BUS.TriggerEvent('popup', {
                    text: this.$LANG.Get('alerts.sequencesTooManyDefaults'),
                    type: 'alert'
                })
            } else {
                this.offerSequencesError = '';
            }

            if (valid) {
                this.client.fiskal_password = this.newFiskalPassword;
                let vm = this;
                this.$API.APICall('put', 'client', this.client, (data, success) => {
                    if (success) {
                        vm.newFiskalPassword = '';
                        if (vm.client.fiskal_password !== '') {
                            vm.client.fiskal_password_set = true;
                            vm.client.fiskal_password = '';
                        }
                        vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                        vm.$API.APICall('get', 'session', {}, (data, success) => {
                            if (success) {
                                vm.$store.commit('setUser', data.user);
                            }
                        }, false);
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                });
                this.$API.APICall('put', 'invoice-sequences', this.sequences.invoice, (data, success) => {
                    if (success) {
                        vm.sequences.invoice = data;
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                });
                this.$API.APICall('put', 'offer-sequences', this.sequences.offer, (data, success) => {
                    if (success) {
                        vm.sequences.offer = data;
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                });
                this.$API.APICall('put', 'receipt-note-sequences', this.sequences.receipt_note, (data, success) => {
                    if (success) {
                        vm.sequences.receipt_note = data;
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                });
            }
        }
    },
    mounted() {

        if (!this.$store.state.user.permissions.includes('ADMINISTRATOR')) {
            this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('alerts.unauthorized'), type: 'alert'});
            this.$router.push('/');
        }
        this.$store.commit('selectMenu', 'settings');
        this.$store.commit('selectSubMenu', 'financialSettings');
        this.$store.commit('setBreadcrumbs', [
            {
                label: this.$LANG.Get('modules.settings'),
                link: null
            },
            {
                label: this.$LANG.Get('modules.financialSettings'),
                link: null
            }
        ]);

        let vm = this;
        this.$API.APICall('get', 'client', {}, (data, success) => {
            if (success) {
                if (data.client !== null) {
                    vm.client.Fill(data.client);
                    vm.initDocumentFormatMap();
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
            vm.$nextTick(() => {
                vm.loaded++;
            });
        });
        this.$API.APICall('get', 'select/payment-methods', {}, (data, success) => {
            if (success) {
                vm.paymentMethods = data;
                vm.initDocumentFormatMap();
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
        });
        this.$API.APICall('get', 'select/document-formats', {}, (data, success) => {
            if (success) {
                vm.documentFormats = data;
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
        });
        this.$API.APICall('get', 'invoice-sequences', {}, (data, success) => {
            if (success) {
                vm.sequences.invoice = data;
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
            vm.$nextTick(() => {
                vm.loaded++;
            });
        });
        this.$API.APICall('get', 'offer-sequences', {}, (data, success) => {
            if (success) {
                vm.sequences.offer = data;
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
            vm.$nextTick(() => {
                vm.loaded++;
            });
        });
        this.$API.APICall('get', 'receipt-note-sequences', {}, (data, success) => {
            if (success) {
                vm.sequences.receipt_note = data;
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
            vm.$nextTick(() => {
                vm.loaded++;
            });
        });
    }
}
</script>

<style scoped>
.form {
    max-width: 800px;
    margin-bottom: 10px;
}

.half {
    width: 50% !important;
}

.document-formats-per-payment {
    display: flex;
    flex-wrap: wrap;
}

.document-formats-title {
    display: block;
    width: 100%;
    font-size: 12px;
    color: #5e6278;
    margin: 5px 0;
}

.templates {
    max-width: 600px;
    position: fixed;
    right: 10px;
    top: 108px;
}

.sequences {
    margin: 5px;
    width: calc(100% - 10px);
    border-collapse: collapse;
    box-sizing: border-box;
}

.sequences th {
    border: 1px solid #b2b2b2;
    padding: 10px;
}

.sequences td {
    border: 1px solid #b2b2b2;
    padding: 10px;
}

.sequences-location-column {
    width: 50px;
}

.sequences-device-column {
    width: 50px;
}

.sequences-actions {
    width: 40px;
    text-align: center;
}

.sequences-checkbox-column {
    width: 20px;
    text-align: center;
}

.sequences-actions span {
    cursor: pointer;
}

.sequence-next-label {
    padding: 5px;
}


:deep(.template-label) {
    font-weight: bold;
}

.disclaimer {
    margin: 5px;
}

.saved-password-disclaimer {
    color: #5e6278;
}

.financial-settings-disclaimer {
    margin-top: 5px;
    margin-bottom: 5px;
    padding: 5px;
    background-color: red;
    max-width: 710px;
    color: white;
}

@media only screen and (max-width: 1580px) {
    .templates {
        max-width: 400px;
    }
}

@media only screen and (max-width: 1420px) {
    .templates {
        position: relative;
        max-width: 600px;
        top: 0;
        right: 0;
    }
}

@media only screen and (max-width: 600px) {
    .half {
        width: 100% !important;
    }
}

</style>